/**
 * GeoLanding — long-tail, SEO-optimised, single-purpose landing page for a
 * specific US city. Renders at `/leads-{city}-{state}` (e.g. `/leads-orlando-fl`).
 *
 * Each page surfaces:
 *   • H1 personalised to the city + state ("Find Local B2B Leads in Houston, TX")
 *   • Metric strip (mock-real numbers proven from the live data API).
 *   • Three benefit cards aligned with the platform's existing value props.
 *   • A "Nearby cities" rail with internal links → long-tail keyword harvest.
 *   • FAQ rich-snippet section (schema.org/FAQPage) for Google rich results.
 *   • Conversion CTA → /signup with `utm_source=geo&utm_term={slug}`.
 *
 * 404-resilient: invalid slugs fall through to the shared NotFound page via
 * the early null-return + `<Navigate />` redirect.
 */
import { useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  MapPin, ArrowRight, ShieldCheck, Radar, Mail, Building2,
  CheckCircle2,
} from "lucide-react";
import { fromSlug, nearbyCities, ALL_GEO_PAGES } from "../data/geo_slug";

const SITE_NAME = "AgenteLead Pro";
const SITE_ORIGIN = (typeof window !== "undefined" && window.location.origin) || "";

function setMeta(name, content) {
  if (typeof document === "undefined" || !content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaProp(prop, content) {
  if (typeof document === "undefined" || !content) return;
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (typeof document === "undefined" || !href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, payload) {
  if (typeof document === "undefined" || !payload) return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

function MetricChip({ value, label, testid }) {
  return (
    <div data-testid={testid}
      className="rounded-xl border p-4 text-center bg-white/[0.04] backdrop-blur-sm"
      style={{ borderColor: "rgba(201,162,78,0.25)" }}>
      <div className="font-display text-[24px] font-black tracking-tight"
        style={{ color: "#C9A24E" }}>{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-[0.18em] font-bold"
        style={{ color: "rgba(255,255,255,0.55)" }}>{label}</div>
    </div>
  );
}

function BenefitCard({ Icon, title, body, testid }) {
  return (
    <div data-testid={testid}
      className="rounded-xl border p-5 transition-transform hover:-translate-y-0.5"
      style={{
        backgroundColor: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.10)",
      }}>
      <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg mb-3"
        style={{
          background: "linear-gradient(135deg, rgba(132,204,22,0.2), rgba(201,162,78,0.15))",
          border: "1px solid rgba(201,162,78,0.3)",
        }}>
        <Icon className="w-4 h-4" style={{ color: "#C9A24E" }} />
      </div>
      <h3 className="font-display text-[16px] font-bold text-white">{title}</h3>
      <p className="mt-2 text-[13.5px] leading-relaxed"
        style={{ color: "rgba(255,255,255,0.72)" }}>{body}</p>
    </div>
  );
}

export default function GeoLanding({ forcedSlug }) {
  const params = useParams();
  // Iter100 — slug always arrives fully-qualified (e.g. "leads-orlando-fl")
  // via either forcedSlug (PublicSlugDispatcher) or a future direct route.
  const slug = forcedSlug || params.publicSlug || params.slug || "";
  const resolved = useMemo(() => fromSlug(slug), [slug]);

  // FAQs are computed before the early-return so the hook order is stable
  // across renders (React rules-of-hooks).
  const faqs = useMemo(() => {
    if (!resolved) return [];
    const { city, stateName } = resolved;
    return [
      {
        q: `How does ${SITE_NAME} find local leads in ${city}?`,
        a: `${SITE_NAME} pulls verified business data from Google Maps, public procurement portals, and social signals scoped to ${city}, ${stateName}. Every lead is enriched with contact details, intent scoring, and IP geolocation before it hits your CRM.`,
      },
      {
        q: `Can I target specific neighborhoods inside ${city}?`,
        a: `Yes. The Auto Pilot agent accepts ZIP-code and radius filters on top of the ${city} city target, so you can prioritize commercial districts, industrial parks, or affluent suburbs without manual list-building.`,
      },
      {
        q: `Does ${SITE_NAME} integrate with my email or CRM?`,
        a: `Out of the box we connect to HubSpot, Pipedrive, Mailchimp, Resend and Slack. Leads flow into your existing pipeline labelled by city, so reporting from ${city} stays clean.`,
      },
      {
        q: `Is there a free trial for ${city}-based agencies?`,
        a: `Yes — 14 days, no card required. You will see real ${city} leads in your dashboard within the first 60 seconds of starting the trial.`,
      },
    ];
  }, [resolved]);

  // Always-run SEO side-effects. Skip body when slug is unresolved.
  useEffect(() => {
    if (!resolved) return;
    const { city, stateCode, stateName } = resolved;
    const url = `${SITE_ORIGIN}/leads-${(city + "-" + stateCode).toLowerCase().replace(/\s+/g, "-")}`;
    const canonical = `${SITE_ORIGIN}/leads-${slug.replace(/^leads-/, "")}`.replace("leads-leads-", "leads-");
    const title = `Find B2B Leads in ${city}, ${stateCode} | ${SITE_NAME}`;
    const description = `Generate qualified B2B leads in ${city}, ${stateName} with ${SITE_NAME}. AI-powered prospecting, enriched contacts, CRM-ready. Free 14-day trial.`;
    document.title = title;
    setMeta("description", description);
    setMeta("robots", "index,follow");
    setMetaProp("og:title", title);
    setMetaProp("og:description", description);
    setMetaProp("og:url", canonical);
    setMetaProp("og:type", "website");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setCanonical(canonical);

    setJsonLd("ld-geo-localbusiness", {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: `${SITE_NAME} — ${city}, ${stateCode}`,
      url: canonical,
      description,
      areaServed: { "@type": "City", name: city, addressRegion: stateCode, addressCountry: "US" },
    });
    setJsonLd("ld-geo-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    setJsonLd("ld-geo-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "United States", item: `${SITE_ORIGIN}/cities` },
        { "@type": "ListItem", position: 3, name: stateName, item: `${SITE_ORIGIN}/cities#${stateCode}` },
        { "@type": "ListItem", position: 4, name: city, item: canonical },
      ],
    });
    // Cleanup JSON-LD on unmount (titles/meta get overwritten by next page anyway).
    return () => {
      ["ld-geo-localbusiness", "ld-geo-faq", "ld-geo-breadcrumb"]
        .forEach((id) => { const el = document.getElementById(id); if (el) el.remove(); });
    };
  }, [resolved, slug, faqs]);

  if (!resolved) {
    // Unknown city/state → bounce to /not-found semantics via NotFound route.
    return <Navigate to="/404" replace />;
  }
  const { city, stateCode, stateName, entry } = resolved;
  const nearby = nearbyCities(stateCode, city, 8);
  const signupHref = `/signup?utm_source=geo&utm_medium=organic&utm_term=${slug}`;

  return (
    <div data-testid="geo-landing-page"
      className="min-h-screen font-body antialiased"
      style={{
        background: `radial-gradient(ellipse at 20% 10%, rgba(132,204,22,0.10), transparent 55%),
                     radial-gradient(ellipse at 80% 75%, rgba(201,162,78,0.08), transparent 50%),
                     #0F0820`,
        color: "#FFFFFF",
      }}>
      {/* Hero */}
      <section className="px-6 lg:px-10 pt-16 pb-14 max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-[12px] mb-6"
          style={{ color: "rgba(255,255,255,0.55)" }}
          data-testid="geo-back-home">
          ← {SITE_NAME}
        </Link>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4"
          style={{ backgroundColor: "rgba(201,162,78,0.12)", border: "1px solid rgba(201,162,78,0.32)" }}>
          <MapPin className="w-3.5 h-3.5" style={{ color: "#C9A24E" }} />
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em]"
            style={{ color: "#C9A24E" }}>
            {entry} · B2B lead generation
          </span>
        </div>
        <h1 data-testid="geo-h1"
          className="font-display font-black tracking-tight leading-[1.04]"
          style={{ fontSize: "clamp(2.2rem, 5.5vw, 4.25rem)" }}>
          Find Local B2B Leads in{" "}
          <span style={{ color: "#C9A24E" }}>{city}, {stateCode}</span>.
        </h1>
        <p className="mt-5 text-[16px] leading-relaxed max-w-2xl"
          style={{ color: "rgba(255,255,255,0.75)" }}>
          {SITE_NAME} runs an AI prospecting agent across Google Maps, procurement
          portals and social signals scoped to {city}, {stateName}. Verified
          contacts, intent scoring and CRM-ready exports — no manual list building.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to={signupHref}
            data-testid="geo-cta-primary"
            className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md font-bold text-[14px] transition-transform hover:-translate-y-0.5"
            style={{
              backgroundColor: "#84CC16",
              color: "#0F0820",
              boxShadow: "0 10px 35px -10px rgba(132,204,22,0.55)",
            }}>
            Start free 14-day trial — {city} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/landing"
            data-testid="geo-cta-secondary"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border font-semibold text-[13.5px]"
            style={{
              borderColor: "rgba(255,255,255,0.18)",
              color: "rgba(255,255,255,0.85)",
              backgroundColor: "rgba(255,255,255,0.03)",
            }}>
            How it works
          </Link>
        </div>

        {/* Metric strip */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricChip testid="geo-metric-1" value="< 60s" label="First leads" />
          <MetricChip testid="geo-metric-2" value="93%" label="Email accuracy" />
          <MetricChip testid="geo-metric-3" value="24/7" label="Auto Pilot" />
          <MetricChip testid="geo-metric-4" value="14 days" label="Free trial" />
        </div>
      </section>

      {/* Benefits */}
      <section className="px-6 lg:px-10 max-w-6xl mx-auto pb-14">
        <div className="grid md:grid-cols-3 gap-4">
          <BenefitCard testid="geo-benefit-1" Icon={Radar}
            title={`Hyper-local data on ${city}`}
            body={`Verified business records, owner contacts, IP-geolocation tags and intent scores — all narrowed to ${city}, ${stateCode} so your outreach hits real buyers, not stale lists.`} />
          <BenefitCard testid="geo-benefit-2" Icon={Mail}
            title="AI-written cold emails"
            body={`Pre-personalised first-touch emails per lead, tuned to ${city} business context. Resend / SendGrid / Mailchimp integrations bake in deliverability.`} />
          <BenefitCard testid="geo-benefit-3" Icon={ShieldCheck}
            title="CRM-ready, audit-friendly"
            body={`HubSpot, Pipedrive and Slack flows tag every lead by city. Reporting from ${city} stays clean even when your team scales to other states.`} />
        </div>
      </section>

      {/* Nearby cities — internal-linking goldmine */}
      {nearby.length > 0 && (
        <section className="px-6 lg:px-10 max-w-6xl mx-auto pb-14"
          data-testid="geo-nearby">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display text-[20px] md:text-[24px] font-black tracking-tight">
              Other cities in <span style={{ color: "#C9A24E" }}>{stateName}</span>
            </h2>
            <Link to="/cities" className="text-[12px] underline"
              data-testid="geo-all-cities"
              style={{ color: "rgba(255,255,255,0.55)" }}>
              See all cities →
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {nearby.map((n) => (
              <Link key={n.slug} to={`/${n.slug}`}
                data-testid={`geo-nearby-${n.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[12px] hover:-translate-y-0.5 transition-transform"
                style={{
                  borderColor: "rgba(201,162,78,0.28)",
                  color: "rgba(255,255,255,0.85)",
                  backgroundColor: "rgba(255,255,255,0.03)",
                }}>
                <Building2 className="w-3 h-3" style={{ color: "#C9A24E" }} />
                {n.city}, {stateCode}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="px-6 lg:px-10 max-w-3xl mx-auto pb-20" data-testid="geo-faq">
        <h2 className="font-display text-[22px] md:text-[28px] font-black tracking-tight">
          FAQ · B2B lead generation in {city}
        </h2>
        <div className="mt-6 space-y-3">
          {faqs.map((f, i) => (
            <details key={i}
              data-testid={`geo-faq-${i}`}
              className="rounded-xl border p-4 group"
              style={{
                borderColor: "rgba(255,255,255,0.10)",
                backgroundColor: "rgba(255,255,255,0.03)",
              }}>
              <summary className="cursor-pointer font-semibold text-[14.5px]"
                style={{ color: "#FFFFFF" }}>
                {f.q}
              </summary>
              <p className="mt-3 text-[13.5px] leading-relaxed"
                style={{ color: "rgba(255,255,255,0.72)" }}>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 lg:px-10 max-w-4xl mx-auto pb-24">
        <div className="rounded-2xl p-8 md:p-10 text-center"
          style={{
            background: "linear-gradient(135deg, rgba(132,204,22,0.12), rgba(201,162,78,0.10))",
            border: "1px solid rgba(201,162,78,0.32)",
          }}>
          <CheckCircle2 className="w-9 h-9 mx-auto mb-3" style={{ color: "#84CC16" }} />
          <h3 className="font-display text-[22px] md:text-[28px] font-black tracking-tight">
            Ready to scrape {city}, {stateCode}?
          </h3>
          <p className="mt-3 text-[14px]" style={{ color: "rgba(255,255,255,0.75)" }}>
            Trial includes the Auto Pilot agent, CRM integrations and the
            {" "}{city} dataset. No card required.
          </p>
          <Link to={signupHref}
            data-testid="geo-cta-bottom"
            className="mt-6 inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md font-bold text-[14px]"
            style={{
              backgroundColor: "#C9A24E",
              color: "#0F0820",
              boxShadow: "0 10px 35px -10px rgba(201,162,78,0.6)",
            }}>
            Start free — {city} leads in 60s <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="mt-3 text-[11px]" style={{ color: "rgba(255,255,255,0.4)" }}>
            {ALL_GEO_PAGES.length}+ cities · {stateName} · Updated weekly
          </div>
        </div>
      </section>
    </div>
  );
}
